import { memo, useCallback, useMemo } from "react";
import type { Camera, CameraUser } from "@src/features/settings/data/mock-sites";
import { ListRoleSelector } from "./ListRoleSelector";
import { Text, View } from "@src/components";
import { StyleSheet } from "react-native-unistyles";

type UserCamerasListProps = {
  cameras: Camera[];
  onCameraRoleChange: (cameraId: string, userId: string, role: CameraUser["role"]) => void;
  onDeleteCamera?: (cameraId: string) => void;
};

export const UserCamerasList = memo(({ cameras, onCameraRoleChange, onDeleteCamera }: UserCamerasListProps) => {
  const items = useMemo(
    () =>
      cameras.map((camera) => ({
        id: camera.id,
        name: camera.name,
        role: camera.users[0]?.role ?? ("viewer" as const),
      })),
    [cameras],
  );

  const handleRoleChange = useCallback(
    (cameraId: string, role: "manager" | "viewer") => {
      const camera = cameras.find((c) => c.id === cameraId);
      if (camera?.users[0]) {
        onCameraRoleChange(cameraId, camera.users[0].id, role);
      }
    },
    [cameras, onCameraRoleChange],
  );

  if (items.length === 0) {
    return (
      <View style={styles.emptyStateContainer}>
        <Text textStyle="sm/regular" color="textMuted" textAlign="center">
          No permissions for any cameras
        </Text>
      </View>
    );
  }

  return (
    <ListRoleSelector items={items} onRoleChange={handleRoleChange} onPressIcon={onDeleteCamera} iconName="delete" />
  );
});

const styles = StyleSheet.create((theme) => ({
  emptyStateContainer: {
    paddingVertical: theme.spacing["5xl"],
  },
}));
